import { SyntheticEvent } from "react";
import { Action, Store } from "redux";
import { SagaMiddleware, Task } from "redux-saga";

import { ClientType } from "@multiweb/clients/ClientType";
import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { IClientFactory } from "@multiweb/clients/IClientFactory";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { canCallOnlyStaticMethods } from "@multiweb/reactConnectors/LinkButtonConnector/utils";
import { IHasAutorefresh } from "@multiweb/reactConnectors/mixins/types";
import { createLoadNotificationGroupsAction, FormOpenMethod, IBridge, ICryptographicInfo, ISignatureInfo, ModalWindowType, NAME } from "@multiweb/reactCore/bridge/api";
import { EMPTY_ARRAY } from "@multiweb/reactUtils/constants"; // Fix cyklické závislosti. Core modul Bridge se využívá v některých utils metodách.
import { invariant } from "@multiweb/reactUtils/invariant"; // přímý import kvůli cyklické závislosti
import {
    IFormProfile,
    ILanguageExtended,
    ILocalizationItem,
    IMenuData,
    INotificationGroup,
    INotificationItem,
    IOpenedFormHistoryInfo,
    IPublicAttributeInfo,
    IResultMessage,
    IRunMethodResult,
    ITaskResultDetail,
    IUser,
    NotificationGroupId,
    NotificationItemId,
    OpenFormData,
} from "@multiweb/types/contracts";
import {
    CancellationToken,
    EventCallback,
    ExperimentName,
    IAppConfiguration,
    ICustomTitleChangedInfo,
    IDataEventsManager,
    IDefferredWindow,
    IEnumType,
    IFavoriteFormChanged,
    IFormContext,
    IFormProfileChanged,
    IHideCloseButtonChanged,
    IMainMenuItemActionHandler,
    IMenuNavigationConfig,
    IOpenedForm,
    IOpenedFormsHistoryManager,
    IOpenFormModalParams,
    IOWObject,
    IOWObjectList,
    IParticipant,
    IRecordIdentification,
    ITypeFactory,
    IUserActionLogger,
    IVisible,
    IWorkflowMenuItem,
    MenuType,
    RequestContext,
    RunStaticMethodParams,
    RunStaticMethodWithParamsConfig,
} from "@multiweb/types/multiweb";
import { ApplicationModule, Constructor, IAppState, LinkTargetOptions, OpenEditWindowParams, ReactAction, ReactActionId, SagaWithReturnType } from "@multiweb/types/react";
import { ApplicationTheme } from "@multiweb/uiShared/ApplicationTheme";
import { Tail } from "@redux-saga/core/effects";
import {
    createFavoriteFormUpdatedAction,
    createFormClosedAction,
    createFormCustomTitleChangedAction,
    createFormItemCloseRestrictionsUpdatedAction,
    createFormOpenedAction,
    createFormOverlayClickedAction,
    createFormProfileChangedAction,
    createFormSwitchedAction,
    createHistoryFormsInfoUpdatedAction,
} from "../actions";
import { StrictBridge } from "../strict";
import { createIsLocalStorageKeyLoadedActionPattern } from "../utils";
import { formOpenModeToFormOpenType, formOpenModeToMenuType } from "./formOpenModeConversion";
import {
    AppContext,
    ApplicationControlMethods,
    ApplicationStorage,
    AttrValueReader,
    createDefferedWindow,
    createPageUrl,
    CryptoInfo,
    DIContainer,
    DownloadManager,
    ErrorHandler,
    EventHelper,
    FormManager,
    FormManagerProperty,
    IFormManager,
    IHTMLElementWithControl,
    ILiteListFrameConnector,
    INotificationManager,
    InstanceManager,
    IReactFrameConnector,
    IReactFrameConnectorWithMultiselect,
    Localization,
    ModelDescriptionsManager,
    OpenedFormsHistoryManager,
    openUrl,
    OWManager,
    OWObject,
    OWObjectList,
    UIManager,
} from "./oldAppImports";

/**
 * Produkční verze bridge modulu. Využívá funkcionality z {@link StrictBridge}.
 */
class Bridge extends StrictBridge implements IBridge {
    private readonly _logger = LazyLoadingSectionLogger.create(LoggingSection.coreReact);

    /**
     * Proměnná uchovávající store pro vysílání akcí. K této proměnné by se nikdy nemělo přistupovat přímo,
     * k tomu slouží metoda getStore.
     * @private
     */
    private _store: Store<IAppState> | null = null;

    /**
     * Proměnná uchovávající funkci pro spuštění ságy. K této proměnné by se nikdy nemělo přistupovat přímo,
     * k tomu slouží metoda getRunSaga.
     * @private
     */
    private _runSaga: SagaMiddleware["run"] | null = null;

    /**
     * Kořenová sága bridge modulu.
     * @private
     */
    private _saga: SagaWithReturnType | null = null;

    /**
     * Uchovává instanci {@link IClientFactory} pro umožnění lazy vytváření v {@link _getClientFactory}.
     * @private
     */
    private _clientFactory: IClientFactory | null = null;

    /**
     * Získá instanci DIContainer.
     * @private
     */
    public readonly getDIContainer = (): ITypeFactory => {
        return DIContainer.instance;
    };

    /**
     * Získá instanci {@link IClientFactory}. Vytváří novou instanci pouze pokud je
     * metoda volaná poprvé, jinak znovupoužije již vytvořenou.
     * @private
     */
    private readonly _getClientFactory = (): IClientFactory => {
        if (this._clientFactory === null) {
            this._clientFactory = this.getDIContainer().create(IClientFactory);
        }
        return this._clientFactory;
    };

    /**
     * Získá store.
     * @private
     * @throws V případě že store není nastavená vyhazuje chybu.
     */
    private readonly _getStore = () => {
        invariant(this._store, "[Bridge module] Bridge module store must be initialized by setStore method before it can be used.");
        return this._store;
    };

    /**
     * Nastaví ságu bridge modulu. Nemůže být importována staticky kvůli cyklické závislosti. Proto se nastaví až po vytvoření instance.
     * @param saga Kořenová sága bridge modulu.
     */
    public readonly setSaga = (saga: SagaWithReturnType): void => {
        this._saga = saga;
    };

    public get module(): ApplicationModule<typeof NAME> {
        invariant(this._saga !== null, "Bridge module was not properly initialized by setting bridge saga.");
        return {
            NAME,
            saga: this._saga,
        };
    }

    public readonly setStore = (storeParam: Store<IAppState>, runSaga: SagaMiddleware["run"]): void => {
        this._store = storeParam;
        this._runSaga = runSaga;
    };

    public readonly errorHandler = (error: unknown): void => {
        ErrorHandler.instance.handleError(error);
    };

    public readonly dispatchAction = (action: Action): void => {
        this._getStore().dispatch(action);
    };

    public readonly runSaga = <S extends SagaWithReturnType<any[]>>(saga: S, ...args: Parameters<S>): Task => {
        invariant(this._runSaga, "[Bridge module] Bridge module store must be initialized by setStore method before it can be used.");
        return this._runSaga(saga, ...args);
    };

    public readonly getState = (): IAppState => {
        return this._getStore().getState();
    };

    public readonly useSelector = <Fn extends(state: IAppState, ...args: Tail<Parameters<Fn>>) => ReturnType<Fn>>(selector: Fn, ...args: Tail<Parameters<Fn>>): ReturnType<Fn> => {
        return selector(this.getState(), ...args);
    };

    public readonly getDataEventsManager = (): IDataEventsManager | undefined => {
        const diContainer = this.getDIContainer();
        if (diContainer.has(IDataEventsManager)) {
            return diContainer.create(IDataEventsManager);
        } else {
            return undefined;
        }
    };

    public readonly getAppConfiguration = (): IAppConfiguration => {
        return AppContext.instance.configuration;
    };

    public readonly isExperimentEnabled = (experimentName: ExperimentName): boolean => {
        return AppContext.instance.configuration.isExperimentEnabled(experimentName);
    };

    public readonly createIsLocalStorageKeyLoadedActionPattern = createIsLocalStorageKeyLoadedActionPattern;

    public readonly getAttributeInfo = (objectName: string, attributesToCheck: string[]): Promise<readonly IPublicAttributeInfo[]> => {
        return ModelDescriptionsManager.instance.getAttributesInfo(objectName, attributesToCheck);
    };

    public readonly readAttributeValue = AttrValueReader.read;

    public readonly getDataReaderClient = (): IDataReaderClient => {
        return this._getClientFactory().get(ClientType.dataReader);
    };

    public readonly getDocumentDownloadUrlByObjectId = (dataObjectId: string, objectName: string, attributeName: string): Promise<string | null> => {
        return OWManager.getDocumentDownloadUrlByObjectId(dataObjectId, objectName, attributeName/*, TODO: #33786 */);
    };

    public readonly runStaticMethod = ({ className, methodName, userLocKey, formId, requestContext }: RunStaticMethodParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> => {
        return OWManager.runStaticMethod({ className, methodName, userLocKey, formId, requestContext }, cancellationToken);
    };

    public readonly runStaticMethodWithParams = (config: RunStaticMethodWithParamsConfig, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> => {
        return OWManager.runStaticMethodWithParams(config, cancellationToken);
    };

    public readonly createObjectList = (): IOWObjectList => {
        return new OWObjectList();
    };

    public readonly isWFDefinedToClass = (className: string, requestContext?: RequestContext, force?: boolean): Promise<boolean> => {
        return OWManager.isWFDefinedToClass(className, requestContext, force);
    };

    public readonly objectsWFMenu = (objectList: IOWObjectList, requestContext?: RequestContext): Promise<IWorkflowMenuItem[]> => {
        return OWManager.objectsWFMenu(objectList, requestContext);
    };

    public readonly getInstance = (instanceId: string): IOWObject | null => {
        return InstanceManager.getInstance(instanceId);
    };

    public readonly getActivityParticipants = (instanceId: string, objectList: IOWObjectList, activityId: string, requestContext?: RequestContext): Promise<IParticipant[]> => {
        return OWManager.getActivityParticipants(instanceId, objectList, activityId, requestContext);
    };

    public readonly prepareDownload = (url: string): Promise<boolean> => DownloadManager.prepareDownload(url);

    public readonly getEnumData = (enumName: string): Promise<IEnumType> => OWManager.getEnum(enumName);

    public readonly stopPropagationNative = EventHelper.stopPropagation;

    public readonly stopPropagation = (event: SyntheticEvent): void => {
        event.stopPropagation();
        this.stopPropagationNative(event.nativeEvent);
    };

    public readonly generateElementID = (): string => {
        return UIManager.instance.generateElementID();
    };

    public readonly logUserInteractionEvent = (event: Event): Promise<void> => {
        return this.getDIContainer().create(IUserActionLogger)?.logEvent(event);
    };

    /**
     * Vrací komponentu (tscControl), pokud pod daným identifikátorem najde komponentu,
     * která implementuje rozhraní {@link IVisible}.
     *
     * @param componentId Identifikátor komponenty, kterou má funkce vrátit.
     */
    private readonly _getControlComponentWithVisibility = (componentId: string): IVisible | undefined => {
        const controlElement = document.getElementById(componentId);
        if (controlElement instanceof IHTMLElementWithControl && controlElement?.tscControl instanceof IVisible) {
            return controlElement.tscControl;
        }
        return undefined;
    };

    public readonly getControlComponentVisibility = (componentId: string): boolean => {
        const component = this._getControlComponentWithVisibility(componentId);
        if (!component) {
            return true;
        }
        return component.visible;
    };

    public readonly addControlComponentVisibilityChangedListener = (componentId: string, listener: EventCallback<IVisible, boolean>): void => {
        const component = this._getControlComponentWithVisibility(componentId);
        if (component?.visibilityChanged) {
            component.visibilityChanged.add(listener);
        }
    };

    public readonly removeControlComponentVisibilityChangedListener = (componentId: string, listener: EventCallback<IVisible, boolean>): void => {
        const component = this._getControlComponentWithVisibility(componentId);
        if (component?.visibilityChanged) {
            component.visibilityChanged.remove(listener);
        }
    };

    public readonly loadLocalizationItems = (locKeys: readonly string[]): Promise<Map<string, ILocalizationItem>> => {
        return Localization.getLocalizationItems(...locKeys);
    };

    public readonly getSupportedLanguages = (): Promise<readonly ILanguageExtended[]> => {
        return this._getClientFactory().get(ClientType.culture).getSupportedLanguages();
    };

    /**
     * Metoda pro získání react connectoru.
     *
     * @param dataSourceKey Klíč podle kterého najde konkrétní connector. Je shodný s id connectoru.
     * @param ConnectorType Typ hledaného connectoru s vlastní implementací `instanceof`.
     * @returns *Connector* implementující rozhranní. *Undefined* pokud connector není nalazen
     * nebo pokud není instancí zadaného typu.
     */
    private readonly _getReactConnector = <T extends IReactFrameConnector = IReactFrameConnector>(dataSourceKey: string, ConnectorType: Constructor<T>): T | undefined => {
        const connectorElement = document.getElementById(dataSourceKey);

        if (connectorElement && connectorElement instanceof IHTMLElementWithControl && connectorElement.tscControl instanceof ConnectorType) {
            return connectorElement.tscControl;
        }
        return undefined;
    };

    public readonly resetRefreshTimeout = (dataSourceKey: string): void => {
        const connector = this._getReactConnector(dataSourceKey, IReactFrameConnector);
        if (connector instanceof IHasAutorefresh)
            return connector?.resetRefreshTimeout();
    };

    public readonly executeReactConnectorAction = (dataSourceKey: string, actionId: ReactActionId, dataObjectId: string): void => {
        const connector = this._getReactConnector(dataSourceKey, IReactFrameConnector);
        connector?.executeAction(actionId, [dataObjectId], connector.objectName);
    };

    public readonly openReactConnectorDetail = (dataSourceKey: string, dataObjectId: string): Promise<void> => {
        const connector = this._getReactConnector(dataSourceKey, IReactFrameConnectorWithMultiselect);
        if (connector) {
            return connector?.openDetailForm(dataObjectId);
        } else {
            return Promise.resolve();
        }
    };

    public readonly openAttributeEditWindow = (params: OpenEditWindowParams): Promise<void> => {
        const connector = this._getReactConnector(params.dataSourceKey, ILiteListFrameConnector);
        return connector?.openAttributeEditWindow(params);
    };

    public readonly setReactConnectorMultiselectMode = (dataSourceKey: string, multiselectMode: boolean): void => {
        const connector = this._getReactConnector(dataSourceKey, IReactFrameConnector);
        if (connector instanceof IReactFrameConnectorWithMultiselect) {
            connector?.setMultiselectMode(multiselectMode, false);
        }
    };

    public readonly getReactConnectorRowActions = (dataSourceKey: string): ReadonlyArray<ReactAction> => {
        const connector = this._getReactConnector(dataSourceKey, IReactFrameConnector);
        return connector?.getReactRowActions() || EMPTY_ARRAY;
    };

    public readonly getMenuNavigationConfig = (): IMenuNavigationConfig | undefined => {
        const diContainer = this.getDIContainer();
        if (diContainer.has(IMenuNavigationConfig)) {
            return diContainer.create(IMenuNavigationConfig);
        } else {
            return undefined;
        }
    };

    /** ------------------- Package protected metody pro použití v ságách. ---------------------------------*/

    /**
     * Nastaví téma aplikace.
     * @param theme Název tématu které se má nastavit.
     */
    public readonly applyTheme = (theme: ApplicationTheme) => {
        UIManager.instance.applyApplicationTheme(theme);
    };

    /** Zavře všechny formuláře, v případě, že má nějaký formulář rozeditované změny, zobrazí YNC dialog. */
    public readonly closeAllForms = () => FormManager.instance.closeAllForms();

    /** Zjistí, zda je některý z formulářů rozeditovaný. */
    public readonly isAnyFormEdited = () => FormManager.instance.isAnyFormEdited();

    /**
     * Pokusí se zavřít formulář. Formulář nemusí být zavřen, pokud obsahuje neuložené změny.
     * @param formInstanceId Identifikátor otevřené instance formuláře.
     */
    public readonly closeForm = (formInstanceId: string) => FormManager.instance.closeForm(formInstanceId);

    /**
     * Získá hodnotu klíče z local storage.
     * @param key Klíč.
     */
    public readonly getLocalStorageKey = (key: string) => ApplicationStorage.get(key);

    /**
     * Získá implementaci pro zpracování custom akcí.
     * @throws Pokud není zaregistrována žádná implementace pro zpracování custom akcí.
     */
    public readonly getMainMenuItemActionHandler = (): IMainMenuItemActionHandler => this.getDIContainer().create<IMainMenuItemActionHandler>(IMainMenuItemActionHandler);

    /**
     * Skryje otevřené formuláře.
     */
    public readonly hideAllForms = () => FormManager.instance.hideAllForms();

    /**
     * Skryje overlay nad formulářovou částí.
     */
    public readonly hideFormOverlay = () => UIManager.instance.hideMenuOverlay();

    /**
     * Skryje načítací indikátor pro celou aplikaci.
     */
    public readonly hideLoadingIndicator = () => UIManager.instance.hideLoader();

    /**
     * Posluchač na změnu hodnoty custom title ve formuláři. Při aktivaci vyšle odpovídající redux akci.
     * @param _sender Odesílatel události.
     * @param customTitleChangedInfo
     */
    private readonly _customTitleChangedListener = (_sender: IFormManager, customTitleChangedInfo: ICustomTitleChangedInfo) => {
        const { formInstanceId, customTitle } = customTitleChangedInfo;
        this.dispatchAction(createFormCustomTitleChangedAction(formInstanceId, customTitle));
    };

    /**
     * Zapojí posluchač na změnu hodnoty custom title ve formulářích.
     */
    public readonly initializeCustomTitleChangedListener = () => FormManager.instance.formCustomTitleChanged.add(this._customTitleChangedListener);

    /**
     * Odpojí posluchač na změnu hodnoty custom title ve formulářích.
     */
    public readonly removeCustomTitleChangedListener = () => FormManager.instance.formCustomTitleChanged.remove(this._customTitleChangedListener);

    /**
     * Posluchač na změny v oblíbených formulářích. Při aktivaci vyšle odpovídající redux akci.
     *
     * @param _sender Odesílatel události.
     * @param menuId ID položky menu.
     * @param formCode Kód změněného formuláře.
     * @param isFavorite True, pokud byl nastaven jako oblíbený. False pokud byl z oblíbených odebrán.
     * @param profile Profil, je undefined pokud není nastaven.
     */
    private readonly _favoriteFormListener = (_sender: IFormManager, { menuId, formCode, isFavorite, profile }: IFavoriteFormChanged) => {
        this.dispatchAction(createFavoriteFormUpdatedAction(menuId, formCode, isFavorite, profile));
    };

    /**
     * Zapojí posluchač na změny oblíbených formulářů do původní aplikace.
     */
    public readonly initializeFavoriteFormListener = () => FormManager.instance.favoriteFormSet.add(this._favoriteFormListener);

    /**
     * Odpojí posluchač na změny oblíbených formulářů do původní aplikace.
     */
    public readonly removeFavoriteFormListener = () => FormManager.instance.favoriteFormSet.remove(this._favoriteFormListener);

    /**
     * Posluchač na zavření formuláře. Při aktivaci vyšle odpovídající redux akci.
     * @param _sender Odesílatel události.
     * @param closedForm Informace identifikující zavřený formulář.
     */
    private readonly _formClosedListener = (_sender: IFormManager, closedForm: IOpenedForm) => {
        this.dispatchAction(createFormClosedAction(closedForm.openedFormInfo));
    };

    /**
     * Zapojí posluchač na zavření formuláře.
     */
    public readonly initializeFormClosedListener = () => FormManager.instance.formClosed.add(this._formClosedListener);

    /**
     * Odpojí posluchač na zavření formuláře.
     */
    public readonly removeFormClosedListener = () => FormManager.instance.formClosed.remove(this._formClosedListener);

    /**
     * Posluchač na změny v omezení akce zavření instance formulářové položky.
     * @param _sender Odesílatel události.
     * @param hideCloseButtonChanged Informace o změně restrikce.
     */
    private readonly _formItemInstanceCloseRestrictionListener = (_sender: IFormManager, hideCloseButtonChanged: IHideCloseButtonChanged) => {
        const { formInstanceId, hideCloseButton } = hideCloseButtonChanged;
        this.dispatchAction(createFormItemCloseRestrictionsUpdatedAction(formInstanceId, hideCloseButton));
    };

    /**
     * Zapojí posluchač na změny v omezení akce zavření instance formulářové položky.
     */
    public readonly initializeFormItemInstanceCloseRestrictionListener = () => FormManager.instance.formHideCloseButtonChanged.add(this._formItemInstanceCloseRestrictionListener);

    /**
     * Odpojí posluchač na změny v omezení akce zavření instance formulářové položky.
     */
    public readonly removeFormItemInstanceCloseRestrictionListener = () => FormManager.instance.formHideCloseButtonChanged.remove(this._formItemInstanceCloseRestrictionListener);

    /**
     * Posluchač na otevření formuláře. Při aktivaci vyšle odpovídající redux akci.
     * @param _sender Odesílatel události.
     * @param openedForm Informace identifikující otevřený formulář.
     */
    private readonly _formOpenedListener = (_sender: IFormManager, openedForm: IOpenedForm) => {
        // @ts-ignore - TODO - odebrat ignore až se vyřeší strict mode pro IOpenedForm, viz types/multiweb/index.ts
        const hideWindowActions = !!openedForm.formGroup.form.hideWindowActions;
        this.dispatchAction(createFormOpenedAction(openedForm.openedFormInfo, hideWindowActions, openedForm.openInBackground));
    };

    /**
     * Zapojí posluchač na otevření formuláře.
     */
    public readonly initializeFormOpenedListener = () => FormManager.instance.formOpened.add(this._formOpenedListener);

    /**
     * Odpojí posluchač na otevření formuláře.
     */
    public readonly removeFormOpenedListener = () => FormManager.instance.formOpened.remove(this._formOpenedListener);

    /**
     * Posluchač na kliknutí na overlay nad formulářovou částí.
     */
    private readonly _formOverlayClickedListener = () => {
        this.dispatchAction(createFormOverlayClickedAction());
    };

    /**
     * Zapojí posluchač na kliknutí na overlay nad formulářovou částí.
     */
    public readonly initializeFormOverlayClickedListener = () => UIManager.instance.menuOverlayClicked.add(this._formOverlayClickedListener);

    /**
     * Odpojí posluchač na kliknutí na overlay nad formulářovou částí.
     */
    public readonly removeFormOverlayClickedListener = () => {
        UIManager.instance.menuOverlayClicked.remove(this._formOverlayClickedListener);
    };

    /**
     * Posluchač na změny profilů formulářů.
     * @param _sender Odesílatel události.
     * @param formProfileChangedArgs Data o změněném profilu
     */
    private readonly _formProfileChangedListener = (_sender: IFormManager, { type, profile }: IFormProfileChanged) => {
        this.dispatchAction(createFormProfileChangedAction(type, profile));
    };

    /**
     * Zapojí posluchač
     * @see _formProfileChangedListener
     */
    public readonly initializeFormProfileChangedListener = () => FormManager.instance.formProfileChanged.add(this._formProfileChangedListener);

    /**
     * Odpojí posluchač.
     * @see _formProfileChangedListener
     */
    public readonly removeFormProfileChangedListener = () => FormManager.instance.formProfileChanged.remove(this._formProfileChangedListener);

    /**
     * Posluchač na přepnutí formuláře. Při aktivaci vyšle odpovídající redux akci.
     * @param _sender Odesílatel události.
     * @param switchedForm Informace identifikující přepnutý formulář.
     */
    private readonly _formSwitchedListener = (_sender: IFormManager, switchedForm: IOpenedForm) => {
        this.dispatchAction(createFormSwitchedAction(switchedForm.openedFormInfo));
    };

    /**
     * Zapojí posluchač na přepnutí formuláře.
     */
    public readonly initializeFormSwitchedListener = () => FormManager.instance.formSwitched.add(this._formSwitchedListener);

    /**
     * Odpojí posluchač na přepnutí formuláře.
     */
    public readonly removeFormSwitchedListener = () => FormManager.instance.formSwitched.remove(this._formSwitchedListener);

    /**
     * Posluchač na změny v historii otevřených formulářů.
     * @param _sender Odesílatel události.
     * @param openedFormHistory Nová data historie.
     */
    private readonly _historyFormsInfoListener = (_sender: OpenedFormsHistoryManager, openedFormHistory: IOpenedFormHistoryInfo[]) => {
        this.dispatchAction(createHistoryFormsInfoUpdatedAction(openedFormHistory));
    };

    /**
     * Zapojí posluchač na změny historie otevřených formulářů do původní aplikace.
     */
    public readonly initializeHistoryFormsInfoListener = () => (
        this.getDIContainer().create(IOpenedFormsHistoryManager).formHistoryUpdated.add(this._historyFormsInfoListener)
    );

    /**
     * Odpojí posluchač na změny historie otevřených formulářů do původní aplikace.
     */
    public readonly removeHistoryFormsInfoListener = () => (
        this.getDIContainer().create(IOpenedFormsHistoryManager).formHistoryUpdated.remove(this._historyFormsInfoListener)
    );

    /** Funkce pro získání instance manažera notifikací. */
    private readonly _getNotificationManager = () => this.getDIContainer().create(INotificationManager);

    /**
     * Funkce volaná při změně notifikačních skupin.
     */
    private readonly _notificationGroupChangedListener = () => this.dispatchAction(createLoadNotificationGroupsAction());

    /**
     * Zapojí posluchač na změnu notifikačních skupin.
     */
    public readonly initializeNotificationGroupsListener = () => (
        this._getNotificationManager().addNotificationGroupListener(this._notificationGroupChangedListener)
    );

    /**
     * Odpojí posluchač na změnu notifikačních skupin.
     */
    public readonly removeNotificationGroupsListener = () => (
        this._getNotificationManager().removeNotificationGroupListener(this._notificationGroupChangedListener)
    );

    /**
     * Získá pole "formCode" formulářů, které jsou nastavené jako oblíbené.
     */
    public readonly loadFavoriteForms = () => FormManager.instance.getProperty(FormManagerProperty.favoriteForm);

    /**
     * Získá historii otevřených formulářů.
     */
    public readonly loadHistoryFormsInfo = () => this.getDIContainer().create(IOpenedFormsHistoryManager).getOpenedFormsHistory();

    /**
     * Vrátí pole id aktivních rolí pro současného uživatele.
     */
    private readonly _getActiveUserRolesIds = (): string[] => AppContext.instance.activeUserRolesIds;

    /**
     * Načte data položek pro menu pomocí MenuClient.
     */
    public readonly loadMenuItems = (): Promise<IMenuData> => {
        const requestContext: RequestContext = {
            roles: this._getActiveUserRolesIds(),
            getTransactionContextId: () => undefined, // Transakční kontext se na menu nevztahuje.
            cancellationToken: CancellationToken.none,
        };
        return this._getClientFactory().get(ClientType.menu).getMenuItems(requestContext);
    };

    /**
     * Načte notifikační skupiny.
     */
    public readonly loadNotificationGroups = (): Promise<readonly Readonly<INotificationGroup>[]> => this._getNotificationManager().getNotificationGroups();

    /**
     * Načte notifikační položky.
     * @param notificationGroupId Identifikátor notifikační skupiny notifikačních položek
     * @param startIndex Počáteční index načítaných notifikačních položek
     * @param itemsCount Počet načítaných notifikačních položek
     */
    public readonly loadNotificationItems = (notificationGroupId: NotificationGroupId, startIndex: number, itemsCount: number): Promise<readonly Readonly<INotificationItem>[]> => (
        this._getNotificationManager().getNotificationItems(notificationGroupId, false, startIndex, itemsCount)
    );

    /**
     * Načte detail o výsledku úlohy.
     * @param objId Identifikátor otevíraného objektu.
     */
    public readonly loadTaskResultDetail = (objId: number): Promise<ITaskResultDetail> => {
        const requestContext: RequestContext = {
            roles: this._getActiveUserRolesIds(),
            getTransactionContextId: () => undefined, // K 1. únoru 2021 se používá pouze v menu, na které se transakční kontext nevztahuje.
            cancellationToken: CancellationToken.none,
        };
        return this._getClientFactory().get(ClientType.tasks).getResultDetails(objId.toString(), requestContext);
    };

    /**
     * Načte data o uživateli z původní části aplikace.
     */
    public readonly loadUserInfo = (): Promise<IUser> => this._getClientFactory().get(ClientType.user).getCurrentUserInfo();

    /**
     * Odhlásí uživatele.
     */
    public readonly logout = () => ApplicationControlMethods.logout();

    /**
     * Interní funkce pro sestavení konfiguračního objektu pro otevření formuláře.
     * @param formId
     * @param menuType
     * @param openFormData Volitelná data využívaná při otevírání formuláře.
     * @param openFormData.profileId Profil, který se má aplikovat na formulář.
     * @param openFormData.objId Identifikátor otevíraného objektu.
     */
    private readonly _getParametricData = (formId: number, menuType: MenuType | null, openFormData: OpenFormData) => {
        const { profileId, selectedId, condition, isFromMainMenu, visibleOnlyDetail, resultMessages, formContext } = openFormData;
        const parametricData: {menuItemId: number; purposeType?: MenuType; applyFormProfileId?: number; selectedID?: string; condition?: string; isFromMainMenu?: boolean; visibleOnlyDetail?: boolean; messages?: readonly IResultMessage[]; formContext?: IFormContext} = { menuItemId: formId };
        if (menuType) {
            parametricData.purposeType = menuType;
        }
        if (profileId) {
            parametricData.applyFormProfileId = profileId;
        }
        if (selectedId) {
            parametricData.selectedID = selectedId;
        }
        if (condition) {
            parametricData.condition = condition;
        }
        if (isFromMainMenu) {
            parametricData.isFromMainMenu = isFromMainMenu;
        }
        if (formContext) {
            parametricData.formContext = formContext;
        }
        if (visibleOnlyDetail) {
            parametricData.visibleOnlyDetail = visibleOnlyDetail;
        }
        if (resultMessages) {
            parametricData.messages = resultMessages;
        }

        return parametricData;
    };

    /**
     * Otevře zadaný formulář.
     * @param formCode Kód formuláře k otevření.
     * @param openMethod Metoda otevření. Určuje, zda je možné využít existující formulář, nebo se vždy otevře nový.
     * @param openFormData Volitelná data využívaná při otevírání formuláře.
     * @param openFormData.profileId Profil, který se má aplikovat na formulář.
     */
    public readonly openForm = (formCode: string, openMethod: FormOpenMethod, openFormData: OpenFormData) => {
        const previewId = FormManager.instance.previewId;
        const { profileId, openMode, menuItemId, selectedId, visibleOnlyDetail, formContext } = openFormData;
        if (formCode) {
            if (openMethod === FormOpenMethod.inTab) {
                const roles = formContext?.roles?.join(",");
                const formUrl = createPageUrl({
                    formCode,
                    formOpenType: openMode ? formOpenModeToFormOpenType(openMode) : undefined,
                    menuItemId,
                    formProfileId: profileId,
                    selectedId,
                    visibleOnlyDetail,
                    previewId,
                    roles,
                });
                openUrl(formUrl, LinkTargetOptions.blank);
            } else {
                const menuType = openMode ? formOpenModeToMenuType(openMode) : null;
                const parametricData = this._getParametricData(menuItemId, menuType, openFormData);
                FormManager.instance.openForm(formCode, parametricData, openMethod !== FormOpenMethod.new);
            }
        } else {
            this._logger.error("Cannot open form without valid formCode or openMode: %O", { formCode, openMode });
        }
    };

    /**
     * Otevře danou URL adresu
     * @param url URL adresa
     * @target taret Specifikace cílu otevření
     */
    public readonly openUrl = (url: string, target?: string): void => {
        openUrl(url, target);
    };

    /**
     * Otevře modální okno
     * @param params Parametry pro otevření modálního okna
     */
    public readonly openFormModal = (params: IOpenFormModalParams) => {
        FormManager.instance.openFormModal({
            formName: params.formName,
            type: ModalWindowType.lov,
            parametricData: {
                viewMode: params.viewMode,
                inLOVMode: params.inLOVMode,
                enableLOVSelect: params.enableLOVSelect,
                condition: params.condition,
                conditionParamsObject: params.conditionParamsObject as OWObject,
                conditionParamsAttrs: params.conditionParamsAttrs,
                masterLinkToAttr: params.masterLinkToAttr,
                masterObject: params.masterObject as OWObject,
            },
            onClose: (_window, result) => {
                if (params.onClose) {
                    if (result?.resultValue instanceof IRecordIdentification) {
                        const { className, id } = result.resultValue;
                        this.getDataReaderClient()
                            .getDataRowsWithParams(className, params.attributeNames, "ID = @0", { count: 1, params: [id] })
                            .then(rows => rows?.length !== 1 ? undefined : params.attributeNames.map(attrName => rows[0][attrName]))
                            .then(attrValues => params.onClose(attrValues));
                    } else {
                        params.onClose(undefined);
                    }
                }
            },
        });
    };

    /**
     * Nastaví, zda je formulář oblíbený nebo ne.
     * @param menuId ID položky menu.
     * @param formCode Kód formuláře, který se nastaví.
     * @param isFavorite True pokud má být formulář oblíbeným, false pokud se má z oblíbených odebrat.
     * @param profile Profil formuláře.
     */
    public readonly setFavoriteForm = (menuId: number, formCode: string, isFavorite: boolean, profile?: IFormProfile) => {
        FormManager.instance.setProperty(
            FormManagerProperty.favoriteForm,
            { menuId, formName: formCode, value: isFavorite, profile });
    };

    /**
     * Nastaví hodnotu pro klíč v local storage.
     * @param key Klíč.
     * @param value Hodnota která se má nastavit.
     */
    public readonly setLocalStorageKey = (key: string, value: string) => {
        ApplicationStorage.set(key, value);
    };

    /**
     * Označí notifikační položku za přečtenou/nepřečtenou.
     */
    public readonly setNotificationItemAsRead = (notificationItemId: NotificationItemId, isRead: boolean) => (
        this._getNotificationManager().setNotificationAsRead(notificationItemId, isRead)
    );

    /*
     * Označí notifikace dané skupiny za přečtené.
     */
    public readonly setGroupNotificationsAsRead = (notificationGroupId: string) => (
        this._getNotificationManager().setGroupNotificationsAsRead(notificationGroupId)
    );

    /**
     * Zobrazí overlay nad formulářovou částí.
     */
    public readonly showFormOverlay = () => UIManager.instance.showMenuOverlay();

    /**
     * Zobrazí načítací indikátor pro celou aplikaci.
     */
    public readonly showLoadingIndicator = () => UIManager.instance.showLoader();

    /**
     * Zobrazí dialog s nastavením.
     */
    public readonly showSettings = () => ApplicationControlMethods.openSettingsWindow();

    /**
     * Přepne otevřený formulář se zadaným id do popředí.
     * @param formInstanceId Id otevřené instance formuláře.
     */
    public readonly switchToForm = (formInstanceId: string) => FormManager.instance.switchToForm(formInstanceId);

    public isSignatureEmpty(signatureInfo: ISignatureInfo): boolean {
        return CryptoInfo.isEmpty(signatureInfo);
    }

    public isSigned(signatureInfo: ISignatureInfo): boolean {
        return CryptoInfo.isSigned(signatureInfo);
    }

    public isEncrypted(cryptoInfo: ICryptographicInfo): boolean {
        return CryptoInfo.isEncrypted(cryptoInfo);
    }

    public hasSignatureWarning(signatureInfo: ISignatureInfo, withCounterSignatures?: boolean): boolean {
        return CryptoInfo.hasSignatureWarning(signatureInfo, withCounterSignatures);
    }

    public readonly canCallOnlyStaticMethods = (url: string, functions: string[]): boolean => {
        return canCallOnlyStaticMethods(url, functions);
    };

    public createDefferedWindow(): IDefferredWindow {
        return createDefferedWindow();
    }

    public objectWFActionExecuteWithoutInstance(ids: string[], className: string, actionID: string, nextParticipants: IParticipant[], requestContext?: RequestContext): Promise<IRunMethodResult> {
        return OWManager.objectWFActionExecuteWithoutInstance(ids, className, actionID, nextParticipants, requestContext);
    }
}

export const bridgeInstance = new Bridge();
